/**
 * Vista de una conversación específica — v2.1.
 *
 * Layout 2-col dentro del Shell:
 *   - Col 1 (360px): ConversationsList con la conv activa resaltada.
 *   - Col 2 (flex-1): Thread detail — header card, customer profile bar,
 *     bubbles scroll, composer al pie.
 *
 * Sidekick pane queda detrás de un feature flag (no incluido en esta pasada).
 *
 * TODO Developer AI: cablear `useUserContext` cuando exista el endpoint
 * cross-canal (Semana 7+). Hoy devuelve null por diseño.
 * TODO Developer AI: implementar `useSendMessage` para el composer
 * (textarea + ⌘↵). Hoy es UI sin acción.
 */
import { useMemo, useState, useEffect, useRef } from "react";
import { useParams } from "@tanstack/react-router";
import {
  ChevronLeft,
  EyeOff,
  MoreHorizontal,
  Paperclip,
  Plus,
  Send,
  Smile,
  Sparkles,
  Tag,
  UserPlus,
  X,
} from "lucide-react";
import { ConversationsList } from "@/components/inbox/conversations-list";
import { PostContextBar } from "@/components/inbox/post-context";
import { SavedRepliesPicker } from "@/components/inbox/saved-replies";
import { Avatar } from "@/components/shared/avatar";
import { Kbd } from "@/components/shared/kbd";
import {
  useConversation,
  useMessages,
  useUserContext,
} from "@/hooks/use-conversations";
import { useConversationAction } from "@/hooks/use-take-conversation";
import { useMarkConversationRead } from "@/hooks/use-mark-read";
import { useSendMessage } from "@/hooks/use-send-message";
import { useDeleteComment } from "@/hooks/use-hide-comment";
import { useContact, useUpdateContact, SUGGESTED_TAGS } from "@/hooks/use-contact";
import { usePanelHotkey } from "@/hooks/use-hotkeys";
import { SlaPill } from "@/components/inbox/sla-pill";
import { cn } from "@/lib/cn";
import { formatArrivalAge } from "@/lib/format";
import type { ConversationState, Message, Platform } from "@/types";

const PLATFORM_LABEL: Record<Platform | string, string> = {
  instagram: "IG",
  facebook: "FB",
  whatsapp: "WA",
};

const STATE_LABEL: Record<ConversationState, string> = {
  bot: "Bot",
  human: "Humano",
  escalated: "Escalada",
  closed: "Cerrada",
};

const STATE_CLASSES: Record<ConversationState, string> = {
  bot: "bg-state-bot-bg text-state-bot-text border border-state-bot-border",
  human: "bg-state-human-bg text-state-human-text border border-state-human-border",
  escalated:
    "bg-state-escalated-bg text-state-escalated-text border border-state-escalated-border",
  closed:
    "bg-state-closed-bg text-state-closed-text border border-state-closed-border",
};

function initialsFor(name: string | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

function formatTime(iso: string): string {
  try {
    // Cada mensaje muestra SIEMPRE día + hora (incl. los de hoy), para que el
    // operador tenga el contexto temporal exacto de entrada/salida. La hora
    // exacta con segundos va en el title (hover).
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const opts: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short" };
    if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
    const date = d.toLocaleDateString("es-AR", opts);
    const time = d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
    return `${date} ${time}`;
  } catch {
    return "";
  }
}

// Timestamp exacto (con segundos) para el hover del thread.
function formatExactTs(iso: string): string {
  try {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("es-AR");
  } catch {
    return "";
  }
}

export function ConversationPage() {
  const { conversationId } = useParams({ strict: false }) as {
    conversationId: string;
  };
  const { data: conversation } = useConversation(conversationId);
  const { data: messages } = useMessages(conversationId);
  // Al abrir el thread, marcar la conv como leída/revisada por un humano.
  // Una sola vez por conversationId (el ref evita re-disparos por re-render).
  const markRead = useMarkConversationRead();
  const markedReadRef = useRef<string | null>(null);
  useEffect(() => {
    if (conversation && conversationId && markedReadRef.current !== conversationId) {
      markedReadRef.current = conversationId;
      markRead.mutate({ conversationId });
    }
  }, [conversation, conversationId, markRead]);
  // TODO Developer AI: userContext aún no disponible (sin endpoint cross-canal).
  const { data: _userContext } = useUserContext(conversationId);
  const action = useConversationAction();
  const deleteComment = useDeleteComment();

  const isHumanTaken = conversation?.state === "human";
  const isEscalated = conversation?.state === "escalated";
  const isClosed = conversation?.state === "closed";
  // Tomar control puede aplicarse desde bot OR escalated (en escalated significa
  // "yo me hago cargo manualmente"). Devolver al bot funciona desde human OR
  // escalated (en escalated = cancelar el escalado, vuelve al ciclo del bot).
  const canRelease = isHumanTaken || isEscalated;
  const state = conversation?.state ?? "bot";

  const [escalateOpen, setEscalateOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  // El backend acepta messageId (int) — derivado del último mensaje entrante
  // del thread (formato del id: "{numericId}.in").
  const latestIncoming = useMemo<{ id: number; kind: "dm" | "comment" } | null>(() => {
    if (!messages?.length) return null;
    const incomings = messages.filter((m) => m.direction === "in");
    const last = incomings[incomings.length - 1];
    if (!last) return null;
    const num = parseInt(last.id.split(".")[0] ?? "", 10);
    if (!Number.isFinite(num)) return null;
    return { id: num, kind: (last.kind ?? "dm") as "dm" | "comment" };
  }, [messages]);

  const latestIncomingMessageId = latestIncoming?.id ?? null;
  const latestIsComment = latestIncoming?.kind === "comment";
  const canAct = !!latestIncomingMessageId;

  // commentId del último comentario entrante — habilita el preview del post
  // (Meta resuelve el post padre a partir del comment_id). null para DMs.
  const commentIdForPost = useMemo<string | null>(() => {
    if (!messages?.length) return null;
    const incomingComments = messages.filter(
      (m) => m.direction === "in" && m.kind === "comment" && m.commentId,
    );
    return incomingComments[incomingComments.length - 1]?.commentId ?? null;
  }, [messages]);

  // Hotkeys del thread: T toma control, B devuelve al bot (o cancela escalado),
  // E abre modal de escalar, R focusea el textarea de respuesta.
  usePanelHotkey(
    "take",
    () => {
      if (!latestIncomingMessageId || isHumanTaken || action.isPending) return;
      action.mutate({ messageId: latestIncomingMessageId, action: "take" });
    },
    !!latestIncomingMessageId && !isHumanTaken,
  );
  usePanelHotkey(
    "release",
    () => {
      if (!latestIncomingMessageId || !canRelease || action.isPending) return;
      action.mutate({ messageId: latestIncomingMessageId, action: "release" });
    },
    canRelease && !!latestIncomingMessageId,
  );
  usePanelHotkey(
    "escalate",
    () => {
      if (!canAct || isEscalated || isClosed) return;
      setEscalateOpen(true);
    },
    canAct && !isEscalated && !isClosed,
  );
  usePanelHotkey(
    "focus-reply",
    () => {
      const ta = document.querySelector<HTMLTextAreaElement>(
        "textarea[data-composer-reply]",
      );
      ta?.focus();
    },
    canAct,
  );

  return (
    <div className="flex h-full min-h-0 bg-background">
      <ConversationsList activeConversationId={conversationId} />

      {/* Thread pane: en mobile ocupa toda la pantalla; en md+ comparte con la lista */}
      <section className="flex-1 min-w-0 flex flex-col bg-background w-full md:w-auto">
        {/* Header */}
        <header className="border-b border-subtle bg-surface-1 px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {/* Back button - mobile-ish, visible siempre como buen affordance */}
            <button
              type="button"
              onClick={() => history.back()}
              aria-label="Volver al inbox"
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors"
            >
              <ChevronLeft size={16} strokeWidth={2} />
            </button>

            {/* Avatar — foto real de IG si está disponible, iniciales de fallback. */}
            <Avatar
              name={conversation?.externalUserName}
              src={conversation?.externalUserAvatar}
              size={40}
            />

            <div className="min-w-0">
              <h2 className="font-sans text-lg font-semibold tracking-snug text-text-primary truncate">
                {conversation?.externalUserName ?? "—"}
              </h2>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <span
                  className={cn(
                    "inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-sans font-medium",
                    STATE_CLASSES[state],
                  )}
                >
                  {STATE_LABEL[state]}
                </span>
                {conversation?.platform && (
                  <span className="inline-flex items-center rounded-full border border-default bg-surface-2 px-2 py-0.5 text-2xs font-sans font-medium text-text-secondary">
                    {PLATFORM_LABEL[conversation.platform] ?? conversation.platform}
                  </span>
                )}
                {conversation?.client && (
                  <span className="inline-flex items-center rounded-full border border-default bg-surface-2 px-2 py-0.5 text-2xs font-sans font-medium text-text-secondary">
                    {conversation.client}
                  </span>
                )}
                {conversation &&
                  conversation.state !== "closed" &&
                  conversation.state !== "escalated" && (
                    <SlaPill remainingSec={conversation.slaRemainingSec} className="ml-1" />
                  )}
                {conversation && (
                  <span
                    className="font-mono text-2xs tabular-nums text-text-muted ml-0.5"
                    title={`Llegó: ${new Date(conversation.lastMessageAt).toLocaleString("es-AR")}`}
                  >
                    {formatArrivalAge(conversation.lastMessageAt)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Header actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() =>
                latestIncomingMessageId &&
                action.mutate({
                  messageId: latestIncomingMessageId,
                  action: "take",
                })
              }
              disabled={action.isPending || isHumanTaken || !canAct}
              title={!canAct ? "Sin mensajes en la conversación" : undefined}
              className={cn(
                "inline-flex items-center gap-2 h-9 px-3 rounded-lg",
                "bg-magenta text-white font-sans text-sm font-medium",
                "hover:bg-magenta-hover active:bg-magenta-active transition-colors",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              <UserPlus size={14} strokeWidth={2} />
              <span>{action.isPending ? "Tomando…" : "Tomar control"}</span>
              <Kbd
                keys="T"
                className="bg-white/15 border-white/20 text-white/85"
              />
            </button>

            <button
              type="button"
              onClick={() =>
                latestIncomingMessageId &&
                action.mutate({
                  messageId: latestIncomingMessageId,
                  action: "release",
                })
              }
              disabled={action.isPending || !canRelease || !canAct}
              title={
                isEscalated
                  ? "Cancela el escalado y devuelve al bot"
                  : "Devuelve la conversación al bot"
              }
              className={cn(
                "inline-flex items-center h-9 px-3 rounded-lg",
                "bg-transparent border border-default text-text-primary font-sans text-sm font-medium",
                "hover:bg-surface-2 transition-colors",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              {isEscalated ? "Cancelar escalado" : "Devolver al bot"}
            </button>

            <button
              type="button"
              onClick={() => setEscalateOpen(true)}
              disabled={action.isPending || !canAct || isEscalated || isClosed}
              title={
                isEscalated
                  ? "Ya escalada"
                  : isClosed
                    ? "Conversación cerrada"
                    : "Marcar como escalada — el bot deja de responder hasta resolución humana"
              }
              className={cn(
                "inline-flex items-center h-9 px-3 rounded-lg",
                "bg-transparent border border-default text-text-primary font-sans text-sm font-medium",
                "hover:bg-surface-2 transition-colors",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              Escalar
            </button>

            {/* Solo para comentarios — Meta no permite borrar DMs entrantes. */}
            {!latestIsComment && canAct && (
              <span
                title="Meta no permite eliminar DMs entrantes desde apps de terceros. Mandá mensaje correctivo encima si el bot respondió mal."
                className="hidden md:inline-flex items-center h-9 px-2.5 rounded-md bg-surface-2 border border-default text-2xs font-sans text-text-muted cursor-help"
              >
                DM · sin opción eliminar
              </span>
            )}
            {latestIsComment && (
              <button
                type="button"
                onClick={() => {
                  if (!latestIncomingMessageId) return;
                  if (
                    !confirm(
                      "¿Eliminar este comentario de IG/FB? Operación IRREVERSIBLE — Meta no permite recuperar comentarios borrados.",
                    )
                  )
                    return;
                  deleteComment.mutate({
                    conversationId,
                    messageId: latestIncomingMessageId,
                  });
                }}
                disabled={deleteComment.isPending || !canAct}
                title="Eliminar comentario de la red (irreversible)"
                className={cn(
                  "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg",
                  "bg-transparent border border-error-border text-error-text font-sans text-sm font-medium",
                  "hover:bg-error-bg transition-colors",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                <EyeOff size={14} strokeWidth={1.75} />
                {deleteComment.isPending ? "Eliminando…" : "Eliminar"}
              </button>
            )}

            <div className="relative">
              <button
                type="button"
                aria-label="Más acciones"
                onClick={() => setMoreMenuOpen((v) => !v)}
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-default text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors"
              >
                <MoreHorizontal size={16} strokeWidth={1.75} />
              </button>
              {moreMenuOpen && (
                <>
                  {/* Click-outside */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setMoreMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 w-64 rounded-lg border border-default bg-surface-1 shadow-lg py-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (!latestIncomingMessageId) return;
                        const note = prompt(
                          "¿Dónde respondiste? (ej: IG mobile, FB Business Suite). Dejá vacío para no anotar.",
                          "",
                        );
                        if (note === null) return; // cancelado
                        action.mutate({
                          messageId: latestIncomingMessageId,
                          action: "mark_external",
                          externalNote: note.trim() || undefined,
                        });
                        setMoreMenuOpen(false);
                      }}
                      disabled={!canAct || isClosed}
                      title="Marca el mensaje como respondido aunque la respuesta haya salido por afuera del panel (app de IG, Business Suite, etc.)"
                      className="w-full text-left px-3 py-2 text-sm font-sans text-text-primary hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Marcar como respondida por fuera
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!latestIncomingMessageId) return;
                        if (!confirm("¿Cerrar esta conversación? Queda archivada — el bot no responde más mensajes nuevos en este thread hasta reabrirla.")) return;
                        action.mutate({
                          messageId: latestIncomingMessageId,
                          action: "close",
                          reason: "Cerrada manualmente desde el inbox",
                        });
                        setMoreMenuOpen(false);
                      }}
                      disabled={!canAct || isClosed}
                      className="w-full text-left px-3 py-2 text-sm font-sans text-text-primary hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Cerrar conversación
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        setMoreMenuOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm font-sans text-text-primary hover:bg-surface-2"
                    >
                      Copiar link de la conversación
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {escalateOpen && (
          <EscalateDialog
            onCancel={() => setEscalateOpen(false)}
            onConfirm={(category, reason) => {
              if (!latestIncomingMessageId) return;
              action.mutate({
                messageId: latestIncomingMessageId,
                action: "escalate",
                category,
                reason,
              });
              setEscalateOpen(false);
            }}
            pending={action.isPending}
          />
        )}

        {/* Customer profile bar */}
        <div className="px-6 py-2.5 border-b border-subtle bg-surface-1 flex items-center gap-2 flex-wrap">
          {conversation?.takenBy && (
            <span className="inline-flex items-center rounded-full bg-surface-2 border border-default px-2.5 py-0.5 text-xs font-sans text-text-secondary">
              Tomada por {conversation.takenBy}
            </span>
          )}
          {conversation?.activeSkillVersion && (
            <>
              <span className="text-text-disabled">·</span>
              <span className="inline-flex items-center rounded-full bg-surface-2 border border-default px-2.5 py-0.5 text-xs font-sans text-text-secondary">
                skill <span className="font-mono ml-1">{conversation.activeSkillVersion}</span>
              </span>
            </>
          )}
          <span className="ml-auto font-mono text-xs tabular-nums text-text-disabled hidden md:inline">
            conv #{conversationId.slice(0, 8)}
          </span>
        </div>

        {/* Preview del post + acceso a todos sus comentarios (solo comentarios). */}
        {conversation?.client && commentIdForPost && (
          <PostContextBar clientSlug={conversation.client} commentId={commentIdForPost} />
        )}

        {/* Tags del contacto (cm_contacts). Persisten cross-conversación. */}
        {conversation?.client && conversation?.externalUserId && (
          <ContactTagsBar
            clientSlug={conversation.client}
            externalUserId={conversation.externalUserId}
          />
        )}

        {/* Thread scroll */}
        <div className="flex-1 overflow-y-auto px-6 py-7">
          <div className="max-w-[820px] mx-auto space-y-5">
            {!messages?.length ? (
              <div className="text-center text-sm text-text-muted py-12">
                Sin mensajes en esta conversación todavía.
              </div>
            ) : (
              messages.map((m) => <MessageBubble key={m.id} message={m} />)
            )}
          </div>
        </div>

        {/* Composer */}
        <Composer
          conversationId={conversationId}
          messageId={latestIncomingMessageId}
          isHumanTaken={isHumanTaken}
          canAct={canAct}
          clientSlug={conversation?.client}
          userName={conversation?.externalUserName}
        />
      </section>
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isIn = message.direction === "in";
  const isBot = !isIn && message.origin === "bot";

  if (message.isInternalNote) {
    return (
      <div className="flex justify-center">
        <div className="max-w-[78%] rounded-xl border border-state-escalated-border bg-state-escalated-bg text-state-escalated-text px-4 py-3 font-sans text-sm">
          <div className="font-sans text-2xs font-semibold uppercase tracking-wider mb-1">
            Nota interna · {message.authorName ?? "—"}
          </div>
          <p className="whitespace-pre-wrap leading-snug">{message.body}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-end gap-3",
        isIn ? "justify-start" : "justify-end",
      )}
    >
      {isIn && (
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-surface-3 border border-default flex items-center justify-center font-sans text-2xs font-semibold text-text-secondary">
          {initialsFor(message.authorName ?? undefined)}
        </div>
      )}

      <div
        className={cn(
          "flex flex-col min-w-0 max-w-[78%]",
          isIn ? "items-start" : "items-end",
        )}
      >
        <div
          className={cn(
            "rounded-2xl px-[18px] py-[14px] font-sans text-[15px] leading-6",
            "border border-default",
            isIn
              ? "bg-surface-2 text-text-primary rounded-bl-md"
              : "bg-surface-3 text-text-primary border-strong rounded-br-md",
          )}
        >
          {!isIn && isBot && (
            <div className="text-xs mb-2 flex items-center gap-1.5 text-text-muted">
              <span className="inline-flex items-center rounded-full border border-default bg-surface-2 px-2 py-0.5 text-2xs font-sans font-semibold">
                AI
              </span>
              <span>·</span>
              <span className="font-sans">{message.authorName ?? "bot"}</span>
            </div>
          )}
          {message.body && (
            <p className="whitespace-pre-wrap leading-snug">{message.body}</p>
          )}
          {message.attachments.length > 0 && (
            <MessageAttachments
              attachments={message.attachments}
              hasText={!!message.body}
            />
          )}
          {!message.body && message.attachments.length === 0 && (
            <p className="italic text-text-muted text-sm">(mensaje vacío)</p>
          )}
        </div>

        <div
          title={formatExactTs(message.createdAt)}
          className={cn(
            "mt-1.5 font-mono text-[11px] tabular-nums text-text-muted",
            isIn ? "ml-1" : "mr-1",
          )}
        >
          {formatTime(message.createdAt)}
          {!isIn && " · enviado"}
        </div>
      </div>

      {!isIn && (
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-surface-3 border border-default flex items-center justify-center text-text-secondary">
          {isBot ? (
            <Sparkles size={12} strokeWidth={2} />
          ) : (
            <span className="font-sans text-2xs font-semibold">
              {initialsFor(message.authorName ?? undefined)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Renderiza imágenes / videos / stickers que vienen en un mensaje. Las
 * imágenes son thumbnails clickeables que abren en una pestaña nueva (lightbox
 * proper lo veremos en una pasada futura). Videos: control nativo. Stickers:
 * tratados como imágenes chicas.
 *
 * Los URLs vienen de Meta Graph y son temporary signed URLs — el panel
 * NO hostea media; si caducan, se ve un placeholder.
 */
function MessageAttachments({
  attachments,
  hasText,
}: {
  attachments: Array<{ type: "image" | "video" | "sticker"; url: string }>;
  hasText: boolean;
}) {
  // Si vino texto arriba, separamos con un poquito de margen.
  const wrapClass = cn("grid gap-1.5", hasText && "mt-3");
  // 1 → full, 2 → 2 cols, 3+ → 3 cols.
  const cols =
    attachments.length === 1
      ? "grid-cols-1"
      : attachments.length === 2
        ? "grid-cols-2"
        : "grid-cols-3";

  return (
    <div className={cn(wrapClass, cols)}>
      {attachments.map((att, i) => {
        if (att.type === "video") {
          return (
            <video
              key={`${att.url}-${i}`}
              src={att.url}
              controls
              preload="metadata"
              className="w-full max-h-72 rounded-lg border border-default bg-black object-contain"
              onError={(e) => {
                const el = e.currentTarget;
                el.style.display = "none";
              }}
            />
          );
        }
        // image + sticker: thumbnail clickeable que abre el original.
        const isSticker = att.type === "sticker";
        return (
          <a
            key={`${att.url}-${i}`}
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "block rounded-lg overflow-hidden border border-default bg-surface-2",
              "hover:border-strong transition-colors",
              isSticker ? "w-20 h-20" : "w-full",
            )}
            title={isSticker ? "Sticker" : "Abrir imagen"}
          >
            <img
              src={att.url}
              alt={isSticker ? "Sticker" : "Adjunto"}
              loading="lazy"
              className={cn(
                "block w-full",
                isSticker
                  ? "h-full object-contain p-1"
                  : "max-h-72 object-cover",
              )}
              onError={(e) => {
                // Si Meta ya invalidó la URL, mostramos un placeholder.
                const el = e.currentTarget;
                el.alt = "Imagen no disponible (URL caducada de Meta)";
                el.classList.add("opacity-40");
                el.src =
                  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1' height='1'/>";
              }}
            />
          </a>
        );
      })}
    </div>
  );
}

function Composer({
  conversationId,
  messageId,
  isHumanTaken,
  canAct,
  clientSlug,
  userName,
}: {
  conversationId: string;
  messageId: number | null;
  isHumanTaken: boolean;
  canAct: boolean;
  clientSlug?: string;
  userName?: string | null;
}) {
  const [text, setText] = useState("");
  const [isInternalNote, setIsInternalNote] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const send = useSendMessage();

  // Inserta un snippet (respuesta guardada) en la posición del cursor.
  function insertAtCursor(snippet: string) {
    const el = textareaRef.current;
    if (!el) {
      setText((t) => t + snippet);
      return;
    }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    setText(text.slice(0, start) + snippet + text.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  }

  // Auto-resize del textarea según contenido.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = Math.min(el.scrollHeight, 280) + "px";
  }, [text]);

  function handleSend() {
    if (!messageId || !text.trim() || send.isPending) return;
    send.mutate(
      {
        conversationId,
        messageId,
        body: text.trim(),
        internalNote: isInternalNote,
      },
      {
        onSuccess: () => {
          setText("");
          setIsInternalNote(false);
        },
      },
    );
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  }

  const canSend = canAct && !!text.trim() && !send.isPending;
  const disabledReason = !canAct
    ? "Sin mensajes en la conversación todavía"
    : !isHumanTaken
      ? "Tomá control de la conversación antes de responder"
      : null;

  return (
    <footer className="border-t border-subtle bg-background px-6 py-4">
      <div className="max-w-[820px] mx-auto">
        <div
          className={cn(
            "rounded-xl border bg-surface-1 p-3 shadow-sm transition-colors",
            isInternalNote
              ? "border-state-escalated-border bg-state-escalated-bg focus-within:border-state-escalated-text"
              : "border-default focus-within:border-magenta focus-within:shadow-focus",
          )}
        >
          <textarea
            ref={textareaRef}
            data-composer-reply
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={!isHumanTaken && !isInternalNote}
            placeholder={
              isInternalNote
                ? "Nota interna (solo el equipo la ve)…"
                : isHumanTaken
                  ? "Escribí una respuesta…"
                  : "Tomá la conversación para responder o cambiá a nota interna"
            }
            className={cn(
              "w-full resize-none bg-transparent border-0 outline-none",
              "font-sans text-[15px] leading-6 text-text-primary placeholder:text-text-muted",
              "min-h-[80px] max-h-[280px]",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          />
          <div className="flex items-center justify-between gap-2 mt-2">
            <div className="flex items-center gap-1">
              <SavedRepliesPicker
                clientSlug={clientSlug}
                userName={userName}
                onInsert={insertAtCursor}
                disabled={!isHumanTaken}
              />
              <button
                type="button"
                aria-label="Adjuntar"
                disabled
                title="Adjuntos pendientes"
                className="inline-flex items-center justify-center w-8 h-8 rounded-md text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Paperclip size={16} strokeWidth={1.75} />
              </button>
              <button
                type="button"
                aria-label="Emoji"
                disabled
                title="Emoji picker pendiente"
                className="inline-flex items-center justify-center w-8 h-8 rounded-md text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Smile size={16} strokeWidth={1.75} />
              </button>
              <button
                type="button"
                onClick={() => setIsInternalNote((v) => !v)}
                className={cn(
                  "inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border font-sans text-xs font-medium transition-colors",
                  isInternalNote
                    ? "border-state-escalated-border bg-state-escalated-bg text-state-escalated-text"
                    : "border-default text-text-secondary hover:bg-surface-2 hover:text-text-primary",
                )}
              >
                {isInternalNote ? "Cambiar a respuesta" : "Nota interna"}
              </button>
            </div>
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend && !(isInternalNote && text.trim())}
              className={cn(
                "inline-flex items-center gap-2 h-9 px-4 rounded-lg",
                "bg-magenta text-white font-sans text-sm font-medium",
                "hover:bg-magenta-hover active:bg-magenta-active transition-colors",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              <Send size={14} strokeWidth={2} />
              <span>{send.isPending ? "Enviando…" : isInternalNote ? "Guardar nota" : "Enviar"}</span>
              <Kbd
                keys="⌘↵"
                className="bg-white/15 border-white/20 text-white/85"
              />
            </button>
          </div>
        </div>
        <p className="mt-2 ml-1 font-sans text-xs text-text-muted">
          {isInternalNote ? (
            <>
              Modo <span className="text-state-escalated-text">Nota interna</span> — solo
              el equipo la ve, no se envía a la red
            </>
          ) : disabledReason ? (
            <span className="text-warning-text">{disabledReason}</span>
          ) : (
            <>
              Modo{" "}
              <span className="text-text-secondary">
                {isHumanTaken ? "Humano" : "Bot"}
              </span>{" "}
              · ⌘+Enter para enviar
            </>
          )}
        </p>
      </div>
    </footer>
  );
}

// Categorías genéricas de escalado — cada manual de cliente puede asignarles
// significado específico. Las letras matchean ESCALATION_CATEGORIES del backend.
const ESCALATION_LABELS: Array<{ code: string; label: string }> = [
  { code: "A", label: "A · Reclamo / queja grave" },
  { code: "B", label: "B · Consulta médica o farmacéutica" },
  { code: "C", label: "C · Datos personales sensibles" },
  { code: "D", label: "D · Trato hostil o crisis" },
  { code: "E", label: "E · Pedido fuera de scope" },
  { code: "F", label: "F · Influencer / prensa / colaboración" },
  { code: "G", label: "G · Otro / no clasificable" },
];

function EscalateDialog({
  onCancel,
  onConfirm,
  pending,
}: {
  onCancel: () => void;
  onConfirm: (category: string, reason: string) => void;
  pending: boolean;
}) {
  const [category, setCategory] = useState("G");
  const [reason, setReason] = useState("");
  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/50"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-xl border border-default bg-surface-1 shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-sans text-lg font-semibold text-text-primary">
          Escalar conversación
        </h3>
        <p className="font-sans text-sm text-text-muted mt-1">
          El bot deja de responder en este thread hasta que un humano resuelva.
          La acción queda registrada en el audit log y dispara alertas según
          la severity configurada.
        </p>

        <label className="block mt-4">
          <span className="block font-sans text-2xs uppercase tracking-wider text-text-muted mb-1">
            Categoría
          </span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-9 w-full rounded-md px-2 bg-surface-2 border border-default text-sm text-text-primary focus:outline-none focus:border-magenta"
          >
            {ESCALATION_LABELS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block mt-3">
          <span className="block font-sans text-2xs uppercase tracking-wider text-text-muted mb-1">
            Razón (opcional)
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Contexto breve para quien tome el caso…"
            className="w-full rounded-md px-2 py-1.5 bg-surface-2 border border-default text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-magenta resize-none"
          />
        </label>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="inline-flex items-center h-9 px-3 rounded-lg bg-transparent border border-default text-text-primary font-sans text-sm font-medium hover:bg-surface-2 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() =>
              onConfirm(
                category,
                reason.trim() || `Escalado manualmente · categoría ${category}`,
              )
            }
            disabled={pending}
            className="inline-flex items-center h-9 px-4 rounded-lg bg-magenta text-white font-sans text-sm font-medium hover:bg-magenta-hover transition-colors disabled:opacity-50"
          >
            {pending ? "Escalando…" : "Escalar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Tags persistentes del contacto externo (cm_contacts). Solo controla el
 * estado de tags en este bar; las notas se editan en otra pasada.
 *
 * Visual: chip-list horizontal con tags actuales + botón "+ Agregar tag" que
 * abre un popover con input + sugerencias.
 */
function ContactTagsBar({
  clientSlug,
  externalUserId,
}: {
  clientSlug: string;
  externalUserId: string;
}) {
  const { data: contact } = useContact({ clientSlug, externalUserId });
  const update = useUpdateContact();
  const [addOpen, setAddOpen] = useState(false);
  const [input, setInput] = useState("");
  const tags = contact?.tags ?? [];

  function addTag(raw: string) {
    const t = raw.trim().toLowerCase();
    if (!t) return;
    if (tags.includes(t)) {
      setInput("");
      setAddOpen(false);
      return;
    }
    update.mutate(
      { key: { clientSlug, externalUserId }, tags: [...tags, t] },
      {
        onSuccess: () => {
          setInput("");
          setAddOpen(false);
        },
      },
    );
  }

  function removeTag(t: string) {
    update.mutate({
      key: { clientSlug, externalUserId },
      tags: tags.filter((x) => x !== t),
    });
  }

  return (
    <div className="px-6 py-2 border-b border-subtle bg-surface-1 flex items-center gap-2 flex-wrap">
      <span className="inline-flex items-center gap-1.5 font-sans text-xs text-text-muted">
        <Tag size={11} strokeWidth={1.75} />
        Etiquetas
      </span>
      {tags.length === 0 && (
        <span className="font-sans text-xs text-text-disabled">
          Sin etiquetas. Agregá una para clasificar a este usuario.
        </span>
      )}
      {tags.map((t) => (
        <span
          key={t}
          className="inline-flex items-center gap-1 rounded-full bg-surface-2 border border-default px-2 py-0.5 text-2xs font-sans text-text-primary"
        >
          {t}
          <button
            type="button"
            onClick={() => removeTag(t)}
            aria-label={`Quitar etiqueta ${t}`}
            disabled={update.isPending}
            className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-text-muted hover:text-error-text hover:bg-error-bg transition-colors disabled:opacity-50"
          >
            <X size={9} strokeWidth={2} />
          </button>
        </span>
      ))}
      <div className="relative">
        <button
          type="button"
          onClick={() => setAddOpen((v) => !v)}
          disabled={update.isPending || tags.length >= 10}
          title={tags.length >= 10 ? "Máximo 10 etiquetas por contacto" : undefined}
          className={cn(
            "inline-flex items-center gap-1 rounded-full border border-dashed border-default px-2 py-0.5",
            "text-2xs font-sans text-text-muted hover:text-text-primary hover:bg-surface-2 transition-colors",
            "disabled:opacity-40 disabled:cursor-not-allowed",
          )}
        >
          <Plus size={10} strokeWidth={2} />
          Agregar
        </button>
        {addOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setAddOpen(false)}
            />
            <div className="absolute left-0 top-full mt-1 z-20 w-64 rounded-lg border border-default bg-surface-1 shadow-lg p-3">
              <div className="font-sans text-2xs uppercase tracking-wider text-text-muted mb-2">
                Nueva etiqueta
              </div>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTag(input);
                  } else if (e.key === "Escape") {
                    setAddOpen(false);
                  }
                }}
                placeholder="influencer, reclamo, ..."
                autoFocus
                maxLength={48}
                className="w-full h-8 px-2 text-sm font-sans rounded-md bg-surface-2 border border-default text-text-primary placeholder:text-text-disabled focus:border-magenta focus:outline-none"
              />
              <div className="mt-2 flex flex-wrap gap-1">
                {SUGGESTED_TAGS.filter((s) => !tags.includes(s)).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => addTag(s)}
                    disabled={update.isPending}
                    className="inline-flex items-center rounded-full border border-default bg-surface-2 px-2 py-0.5 text-2xs font-sans text-text-secondary hover:text-text-primary hover:border-strong transition-colors disabled:opacity-50"
                  >
                    + {s}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
